import classNames from 'classnames'
import React, { useCallback, useState } from 'react'
import { JSONFetcher, Modal, gaEvent } from 'shared'
import { SubscribeModalProps, SubscriptionFormProps } from './types'

export const SubscribeModal = (props: SubscribeModalProps): JSX.Element => (
  <Modal
    show={props.show}
    onHide={props.onHide}
    id="project-newsletter-subscription-modal"
    content={<SubscriptionForm {...props} onHide={props.onHide} />}
    header={I18n.t('projects.show.stats.subscribe_modal_headline')}
    headerSubline={I18n.t('projects.show.stats.subscribe_modal_subline')}
  />
)

const SubscriptionForm = (props: SubscriptionFormProps): JSX.Element => {
  const [state, setState] = useState({
    email: '',
    projectNewsletter: true,
    platformNewsletter: true,
    submitted: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: undefined as any,
    showModal: false,
  })

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      if (state.platformNewsletter) gaEvent('engagement', 'submit subscribe_platform_newsletter_form')
      if (state.projectNewsletter) gaEvent('engagement', 'submit subscribe_project_newsletter_form')
      event.preventDefault()
      JSONFetcher.post({
        url: props.url,
        body: {
          email: state.email,
          platform_newsletter: state.platformNewsletter,
          project_newsletter: state.projectNewsletter,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        success: (response: { error?: any }) => {
          if (response.error) {
            setState((old) => ({ ...old, error: response.error }))
            gaEvent('error', 'field: newsletter_email', `error: ${response.error}`, null, true)
          } else {
            setState((old) => ({ ...old, submitted: true, error: undefined }))
          }
        },
      })
    },
    [props.url, state.email, state.platformNewsletter, state.projectNewsletter]
  )

  const onEmailChanged: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => setState((old) => (old.email !== event.target.value ? { ...old, email: event.target.value } : old)),
    []
  )

  const onProjectNewsletterChanged: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => setState((old) => ({ ...old, projectNewsletter: event.target.checked })),
    []
  )

  const onPlatformNewsletterChanged: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => setState((old) => ({ ...old, platformNewsletter: event.target.checked })),
    []
  )

  if (state.submitted) {
    return (
      <div>
        <p>{I18n.t('projects.show.stats.subscribe_modal_thank_you')}</p>
        <button className="btn btn-primary btn-large" onClick={props.onHide}>
          {I18n.t('projects.show.stats.subscribe_modal_close_button')}
        </button>
      </div>
    )
  }

  return (
    <div>
      <form className="simple_form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="control-label" htmlFor="mailing_newsletter_opt_in_email">
            <abbr title="required">*</abbr>
            <span>&nbsp;E-Mail</span>
          </label>
          <input
            className={classNames('form-control', { 'field-with-error': state.error })}
            id="mailing_newsletter_opt_in_email"
            type="text"
            value={state.email}
            onChange={onEmailChanged}
          />
          {state.error && <div className="help-inline">{state.error}</div>}
        </div>

        <div className="form-group">
          <div className="checkbox">
            <label>
              <input type="checkbox" checked={state.projectNewsletter} onChange={onProjectNewsletterChanged} />{' '}
              <span
                dangerouslySetInnerHTML={{
                  __html: I18n.t('projects.show.stats.subscription_project_label', {
                    project_name: props.project.title,
                  }),
                }}
              />
            </label>
          </div>
        </div>

        <div className="form-group">
          <div className="checkbox">
            <label>
              <input type="checkbox" checked={state.platformNewsletter} onChange={onPlatformNewsletterChanged} />{' '}
              <span>{I18n.t('projects.show.stats.subscription_platform_label')}</span>
            </label>
          </div>
        </div>

        <div className="form-actions text-center">
          <input
            type="submit"
            value={I18n.t('projects.show.stats.subscription_modal_button')}
            className="btn btn-primary btn-large"
          />
        </div>

        <div
          className="text-muted mt-4"
          dangerouslySetInnerHTML={{
            __html: I18n.t('projects.show.stats.subscription_modal_disclaimer'),
          }}
        />
      </form>
    </div>
  )
}
